/**
 * Attribution capture utilities
 * Parses UTM parameters and detects traffic sources from referrers
 */

import {
  ATTRIBUTION_QUERY_PARAMS,
  UTM_PARAMS,
  REFERRER_SOURCE_MAP,
  SOURCE_MEDIUM_MAP,
  PAID_INDICATORS,
  OWN_DOMAIN,
  SELF_REFERRAL_HOSTS,
} from "./constants";
import type { AttributionData } from "./types";

/**
 * Parse UTM parameters from a URL search string
 */
export function parseUtmParams(searchParams: URLSearchParams): {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
} {
  return {
    source: searchParams.get("utm_source"),
    medium: searchParams.get("utm_medium"),
    campaign: searchParams.get("utm_campaign"),
    content: searchParams.get("utm_content"),
    term: searchParams.get("utm_term"),
  };
}

/**
 * Check if URL has any paid traffic indicators (click IDs)
 */
export function hasPaidIndicator(searchParams: URLSearchParams): boolean {
  return PAID_INDICATORS.some((param) => searchParams.has(param));
}

export function parseDripClickParams(searchParams: URLSearchParams): {
  dripEmailId: number | null;
  dripStep: number | null;
} {
  const dripEmailIdRaw = searchParams.get("drip_email_id");
  const dripStepRaw = searchParams.get("drip_step");
  const dripEmailId = dripEmailIdRaw
    ? Number.parseInt(dripEmailIdRaw, 10)
    : NaN;
  const dripStep = dripStepRaw ? Number.parseInt(dripStepRaw, 10) : NaN;

  if (
    !Number.isInteger(dripEmailId) ||
    dripEmailId <= 0 ||
    !Number.isInteger(dripStep) ||
    dripStep <= 0
  ) {
    return {
      dripEmailId: null,
      dripStep: null,
    };
  }

  return {
    dripEmailId,
    dripStep,
  };
}

/**
 * Detect source from referrer URL
 */
export function detectSourceFromReferrer(referrer: string): string | null {
  if (!referrer) return null;

  try {
    const url = new URL(referrer);
    const hostname = url.hostname.toLowerCase();

    // Check against known referrer mappings
    if (REFERRER_SOURCE_MAP[hostname]) {
      return REFERRER_SOURCE_MAP[hostname];
    }

    // Try without www prefix
    const withoutWww = hostname.replace(/^www\./, "");
    if (REFERRER_SOURCE_MAP[withoutWww]) {
      return REFERRER_SOURCE_MAP[withoutWww];
    }

    // Return the hostname as source if not in our map
    return withoutWww;
  } catch {
    return null;
  }
}

/**
 * Detect medium based on source and URL parameters
 */
export function detectMedium(
  source: string | null,
  searchParams: URLSearchParams,
): string | null {
  // If we have a paid indicator, it's likely paid traffic
  if (hasPaidIndicator(searchParams)) {
    return "cpc";
  }

  // Use source-to-medium mapping
  if (source && SOURCE_MEDIUM_MAP[source]) {
    return SOURCE_MEDIUM_MAP[source];
  }

  return null;
}

function resolveLandingPage(urlObj: URL): string {
  const redirectTo = urlObj.searchParams.get("redirectTo");
  const hasForwardedAttribution = ATTRIBUTION_QUERY_PARAMS.some((param) =>
    urlObj.searchParams.has(param),
  );

  if (urlObj.pathname === "/login" && redirectTo && hasForwardedAttribution) {
    try {
      if (!redirectTo.startsWith("/") || redirectTo.startsWith("//")) {
        return `${urlObj.origin}${urlObj.pathname}`;
      }

      const redirectUrl = new URL(redirectTo, urlObj.origin);

      if (redirectUrl.origin !== urlObj.origin) {
        return `${urlObj.origin}${urlObj.pathname}`;
      }

      return `${redirectUrl.origin}${redirectUrl.pathname}`;
    } catch {
      // Fall through to the current pathname if redirectTo is malformed.
    }
  }

  return `${urlObj.origin}${urlObj.pathname}`;
}

function isSelfReferral(referrer: string, currentHostname: string): boolean {
  try {
    const hostname = new URL(referrer).hostname.toLowerCase();
    return (
      hostname === currentHostname ||
      hostname === OWN_DOMAIN ||
      hostname.endsWith(`.${OWN_DOMAIN}`) ||
      SELF_REFERRAL_HOSTS.includes(hostname)
    );
  } catch {
    return false;
  }
}

/**
 * Capture attribution data from current page context
 * Should be called on the client side
 */
export function captureAttributionData(
  url: string,
  rawReferrer: string,
): AttributionData {
  const urlObj = new URL(url);
  const referrer = isSelfReferral(rawReferrer, urlObj.hostname)
    ? ""
    : rawReferrer;
  const searchParams = urlObj.searchParams;

  // Parse UTM parameters
  const utmParams = parseUtmParams(searchParams);

  // If we have UTM source, use it; otherwise detect from referrer
  let source = utmParams.source;
  let medium = utmParams.medium;

  if (!source && referrer) {
    source = detectSourceFromReferrer(referrer);
  }

  if (!medium && source) {
    medium = detectMedium(source, searchParams);
  }

  // If still no source, mark as direct
  if (!source && !referrer) {
    source = "direct";
    medium = "none";
  }

  return {
    source,
    medium,
    campaign: utmParams.campaign,
    content: utmParams.content,
    term: utmParams.term,
    referrer: referrer || null,
    landingPage: resolveLandingPage(urlObj),
    capturedAt: new Date().toISOString(),
  };
}

/**
 * Check if new attribution data should update the last touch
 * Only updates if we have meaningful new attribution data
 */
export function shouldUpdateLastTouch(
  newData: AttributionData,
  existingData: AttributionData,
): boolean {
  // If new data has UTM params, always update
  if (newData.source && newData.source !== "direct") {
    return true;
  }

  // If new data has a different referrer source, update
  if (
    newData.referrer &&
    newData.referrer !== existingData.referrer &&
    newData.source !== "direct"
  ) {
    return true;
  }

  return false;
}

/**
 * Check if we have any attribution data worth storing
 */
export function hasAttributionData(data: AttributionData): boolean {
  return !!(
    (data.source && data.source !== "direct") ||
    data.campaign ||
    data.content ||
    data.term ||
    data.referrer
  );
}

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift();
  }
  return undefined;
}

/**
 * Capture Meta (Facebook) tracking cookies
 * - _fbp: Browser ID cookie set by Meta Pixel
 * - _fbc: Click ID cookie created from fbclid URL parameter
 */
export function captureMetaCookies(): { fbp?: string; fbc?: string } {
  const fbp = getCookie("_fbp");
  const fbc = getCookie("_fbc");

  return {
    fbp: fbp || undefined,
    fbc: fbc || undefined,
  };
}
